import React, { useState, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import parser from 'ua-parser-js';
import { DateTime } from 'luxon';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import IconButton from '@material-ui/core/IconButton';

import DevicesIcon from '@material-ui/icons/Devices';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';

import SaveButton from 'app/components/buttons/SaveButton';
import Loading from 'app/components/loaders/Loading';

import PasswordPatterns from 'app/utils/PasswordPatterns';

import useApi from 'app/context/useApi';

import { paper } from 'app/styles/core';
import { useEffect } from 'react';

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(2),
    },
    marginTop: {
        marginTop: theme.spacing(3),
    },
}))

export default function Security(props) {

    const { urlPrefix, user, admin, deleted } = props;
    const classes = useStyles();
    const api = useApi();

    const [ sessions, setSessions ] = useState([]);

    const [ password, setPassword ] = useState("");
    const [ passwordConfirmation, setPasswordConfirmation ] = useState("");
    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState([]);

    const getSessions = () => {
        setLoading(true);

        api.get(urlPrefix + '/security/session')
            .then(res => {

                res.sessions.forEach(session => {
                    session.browser = parser(session.user_agent);
                });

                setSessions(res.sessions);
            })
            .finally(setLoading(false));
    }

    useEffect(() => {
        getSessions();
    }, []);

    const handlePassword = event => {
        const value = event.target.value;
        if (isError('password')) delete errors.password;
        setPassword(value);
    }

    const handlePasswordConfirmation = event => {
        const value = event.target.value;
        if (isError('password_confirmation')) delete errors.password_confirmation;
        setPasswordConfirmation(value);
    };

    const matchesPattern = pattern => {
        return pattern.test(password)
    }

    const checkPassword = (passwordConfirmation === password);

    const postPassword = () => {
        setLoading(true);
        setErrors([]);

        const data = {
            password: password,
            password_confirmation: passwordConfirmation
        }

        api.post(`${urlPrefix}/security/update-password`, data)
            .then(res => {

                setPassword("");
                setPasswordConfirmation("");

            }).catch(error => {

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors);
                }

            }).finally(() => setLoading(false));
    }

    const deleteSession = id => {

        setLoading(true);

        api.delete(urlPrefix + '/security/session/' + id)
            .then(() => getSessions())
            .catch(() => setLoading(false));
    };

    const postPasswordResetEmail = () => {
        setLoading(true);

        api.get(`${urlPrefix}/security/reset/email`).finally(() => setLoading(false));
    }

    const postWelcomeEmail = () => {
        setLoading(true);

        api.get(`${urlPrefix}/security/welcome/email`).finally(() => setLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field][0];
        return "";
    }

    const getSessionPrimaryText = s => {
        return `${s.browser.browser.name ?? 'Unknown Browser'} | ${s.browser.os.name ?? 'Unknown OS'} | ${s.browser.device.model ?? 'Unknown Device'}`
    }

    const getSessionSecondaryText = s => {
        const updated_at = DateTime.fromSQL(s.updated_at, { zone: "utc" }).toLocal();
        const lastUsed = (updated_at.toISODate() === DateTime.local().toISODate()) ? `Used Today at ${updated_at.toLocaleString(DateTime.TIME_WITH_SECONDS)}` : 'Last Used on ' + updated_at.toLocaleString(DateTime.DATETIME_MED);

        const city = (s.location.geoplugin_city && s.location.geoplugin_city != '') ? s.location.geoplugin_city : null;
        const region = (s.location.geoplugin_regionCode && s.location.geoplugin_regionCode != '') ? s.location.geoplugin_regionCode : null;
        const country = (s.location.geoplugin_countryName && s.location.geoplugin_countryName != '') ? s.location.geoplugin_countryName : null;

        if (city && region && country) return `${lastUsed} from ${city}, ${region}, ${country}`;
        if (city && region) return `${lastUsed} from ${city}, ${region}`;
        if (region && country) return `${lastUsed} from ${region}, ${country}`;
        if (country) return `${lastUsed} from ${country}`;
        return lastUsed;
    }

    return (
        <form
            noValidate
            autoComplete="off"
            >
            <Paper className={classes.paper}>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Security
                        </Typography>
                    </Grid>
                    {(admin && user && user.email_verified_at === null) &&
                        <Grid item>
                            <Tooltip
                                title={user.email_verified_at ? 'Can not send welcome email if user has previously logged in.' : 'Send a welcome email to user with a link to login.'}
                                aria-label="send welcome email with login link"
                                >
                                <span>
                                    <Button
                                        variant="outlined"
                                        onClick={postWelcomeEmail}
                                        disabled={loading || !!user?.email_verified_at || deleted}
                                        color="primary"
                                        >
                                        Send Welcome Email
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                    }
                </Grid>
                <Typography color="textSecondary">
                    Here you can update {admin ? `the user's` : `your`} password and adjust other security preferences.
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={loading}/>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3">
                            Password
                        </Typography>
                    </Grid>
                    {admin &&
                        <Grid item>
                            <Button
                                variant="outlined"
                                onClick={postPasswordResetEmail}
                                disabled={loading || deleted}
                                color="primary"
                                >
                                Send Password Reset Email
                            </Button>
                        </Grid>
                    }
                </Grid>
                <Grid container
                    spacing={3}
                    alignItems="flex-start"
                    >
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <Typography gutterBottom variant="subtitle2">
                            Enter new password:
                        </Typography>
                        <TextField
                            id="input-password"
                            label="Password"
                            value={password}
                            onChange={handlePassword}
                            variant="outlined"
                            fullWidth
                            type="password"
                            name="password"
                            margin="normal"
                            error={isError('password')}
                            helperText={getErrorMessage('password')}
                            disabled={deleted}
                        />
                        <TextField
                            id="input-check-password"
                            label="Verify Password"
                            value={passwordConfirmation}
                            onChange={handlePasswordConfirmation}
                            fullWidth
                            variant="outlined"
                            type="password"
                            name="password-confirmation"
                            margin="normal"
                            error={!checkPassword}
                            helperText={checkPassword ? "" : "Passwords do not match"}
                            disabled={deleted}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6}>
                        <Typography variant="subtitle2" className={classes.marginBottom}>
                            New password must:
                        </Typography>
                        {PasswordPatterns.map((p, i) => (
                            <Fragment key={i}>
                                <Typography
                                    gutterBottom
                                    variant="body2"
                                    color={matchesPattern(p.regex) ? 'primary' : 'error'}
                                    >
                                    {p.label}
                                </Typography>
                            </Fragment>
                        ))}
                    </Grid>
                </Grid>
                <Typography align="right" component="div" className={classes.marginTop}>
                    <SaveButton
                        disabled={loading || deleted}
                        variant="contained"
                        color="primary"
                        onClick={postPassword}
                        >
                        Update
                    </SaveButton>
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={loading}/>
                <Grid
                    container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3">
                            {admin ? "User's" : "Your"} Logged in Devices
                        </Typography>
                    </Grid>
                </Grid>
                <List>
                    {sessions.map(s => (
                        <ListItem key={s.id}>
                            <ListItemIcon>
                                <DevicesIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary={getSessionPrimaryText(s)}
                                secondary={getSessionSecondaryText(s)}
                            />
                            <ListItemSecondaryAction>
                                <IconButton edge="end" aria-label="delete" onClick={() => deleteSession(s.id)}>
                                    <HighlightOffIcon color="primary"/>
                                </IconButton>
                            </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                </List>
            </Paper>
        </form>
    )
}
